import { LanguagesEnum } from '@/api/enums';
import { equal, nested } from '@/api/helpers';
import { Course } from '@/client/courses';
import { RedirectPaths, RedirectPathsEnum } from '@/common/constants/routes';
import { LanguageContext } from '@/common/context/LanguageContext';
import { useCourse, useCourseSchedules } from '@/hooks/query/courses.hooks';
import { usePlanners } from '@/hooks/query/planner.hooks';
import { AppButton } from '@/ui/buttons';
import { FlexContainer } from '@/ui/styled-ui';
import { getTranslationByLanguage } from '@/utils/helpers';
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

const BannerContainer = styled(FlexContainer)`
  background: var(--blue-opacity);
  border-radius: 19px;
  padding: 40px;
  width: 100%;
  flex-direction: column;
  align-items: flex-start;
  margin-bottom: 30px;
`;

const BannerRow = styled(FlexContainer)`
  width: 100%;
  gap: 10px;
  align-items: center;
`;

const BannerTextContainer = styled(FlexContainer)`
  flex-direction: row;
  align-items: center;
  flex: 1;
`;

const BannerText = styled(FlexContainer)`
  flex-direction: column;
  gap: 10px;
  align-items: flex-start;
  justify-content: center;
  flex: 1;
`;

const BannerTitle = styled.p`
  font-family: var(--font-family);
  font-weight: 600;
  font-size: 22px;
  line-height: 1.4;
  color: var(--blue-darkest);
  margin: 0;
  white-space: pre-wrap;
`;

const BannerDescription = styled.p`
  font-family: var(--font-family);
  font-weight: 400;
  font-size: 16px;
  line-height: 1.4;
  color: var(--blue-darkest);
  margin: 0;
  white-space: pre-wrap;
`;

const ButtonContainer = styled(FlexContainer)`
  flex: 1 0 0;
  flex-direction: column;
  gap: 10px;
  align-items: flex-end;
  justify-content: center;
  min-width: 0;
  min-height: 0;
`;

const StyledButton = styled(AppButton)`
  &.p-button {
    border-radius: 25px;
    padding: 14px 24px 15px;
    background: var(--blue-darkest) !important;
    border-color: var(--blue-darkest) !important;
    color: white;
    font-weight: 500;
    font-size: 14px;
    line-height: 1.5;
    white-space: nowrap;
    flex-shrink: 0;

    &:hover,
    &:enabled:hover {
      background: #105484 !important;
      border-color: #105484 !important;
    }
  }
`;

export type ReplaceScheduledContentBannerProps = {
  accountId: string;
  accountName: string;
  course: Course;
};

export const ReplaceScheduledContentBanner: React.FC<
  ReplaceScheduledContentBannerProps
> = ({ accountId, accountName, course }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { language } = useContext(LanguageContext);

  const originalCourseId = course.customizationMetadata?.originalContentId;

  // Fetch the original course to get its name
  const { course: originalCourse, isLoading: isLoadingOriginalCourse } =
    useCourse({
      courseId: originalCourseId,
      enabled: !!originalCourseId,
    });

  // Check if original course has scheduled content
  const { schedules, isLoading: isLoadingSchedules } = useCourseSchedules(
    {
      take: 1,
      skip: 0,
      enabled: !!originalCourseId,
      filters: [
        nested('account', [equal('id', accountId)]),
        equal('executedAt', null),
      ],
    },
    originalCourseId,
  );

  const { planners, isLoading: isLoadingPlanners } = usePlanners(
    {
      enabled: !!accountId && !!originalCourseId,
    },
    accountId,
  );

  const isOriginalCoursePlanned = planners?.result?.some((plan) =>
    plan.dynamicCourseSchedules.some(
      (plannerCourseSchedule) =>
        plannerCourseSchedule.course.id === originalCourseId,
    ),
  );

  const hasScheduledContent =
    (schedules?.count ?? 0) > 0 || isOriginalCoursePlanned;

  const handleReplace = () => {
    if (!originalCourseId) return;

    navigate(
      RedirectPaths[RedirectPathsEnum.COURSES_REPLACE_SCHEDULED_CONTENT](
        course.id,
      ),
    );
  };

  // Don't show banner if:
  // - Course is not customized
  // - Original course doesn't have scheduled content
  // - Still loading schedule data or original course
  if (
    !originalCourseId ||
    !hasScheduledContent ||
    isLoadingSchedules ||
    isLoadingOriginalCourse ||
    !originalCourse ||
    isLoadingPlanners
  ) {
    return null;
  }

  return (
    <BannerContainer>
      <BannerRow>
        <BannerTextContainer>
          <BannerText>
            <BannerTitle>
              {t('course.customization.replaceScheduledContent.title')}
            </BannerTitle>
            <BannerDescription>
              {t(
                'course.customization.replaceScheduledContent.bannerDescription',
                {
                  accountName,
                  courseName:
                    getTranslationByLanguage(
                      originalCourse?.courseTranslations,
                      language as LanguagesEnum,
                      originalCourse.defaultLanguage,
                    )?.name || originalCourse.name,
                },
              )}
            </BannerDescription>
          </BannerText>
        </BannerTextContainer>
        <ButtonContainer>
          <StyledButton
            label={t(
              'course.customization.replaceScheduledContent.replaceButton',
            )}
            onClick={handleReplace}
          />
        </ButtonContainer>
      </BannerRow>
    </BannerContainer>
  );
};
